// TelcoTrack - Tabs Component (P1 #7)
// Real WAI-ARIA tabs affordance: role="tablist"/"tab", aria-selected, roving tabindex,
// arrow-key / Home / End navigation. Content panels stay owned by the page (router-outlet,
// *ngIf/ngSwitch blocks, …) — pass `panelId` per tab and set `id`/`role="tabpanel"`/
// `aria-labelledby` on that content to complete the association when practical.
//
// Active highlight is the tab’s own `.tt-tab--active` fill (same chip as Úřady). A detached
// sliding glider was dropped: title + subtitle in the topbar shift layout and left the
// overlay misaligned on Sklady / Materiály.

import { Component, EventEmitter, Input, Output } from '@angular/core';

export interface TabItem {
  id: string;
  label: string;
  disabled?: boolean;
  /** Optional id of the associated tabpanel element (for aria-controls / id linking). */
  panelId?: string;
}

@Component({
  selector: 'app-tabs',
  template: `
    <div
      class="tt-tabs"
      role="tablist"
      [attr.aria-label]="ariaLabel || null"
    >
      <button
        *ngFor="let tab of tabs; let i = index; trackBy: trackTab"
        type="button"
        role="tab"
        class="tt-tab"
        [class.tt-tab--active]="tab.id === activeId"
        [id]="tabDomId(tab)"
        [attr.aria-selected]="tab.id === activeId"
        [attr.aria-controls]="tab.panelId || null"
        [attr.aria-disabled]="tab.disabled ? 'true' : null"
        [tabindex]="tab.id === activeId ? 0 : -1"
        (click)="select(tab)"
        (keydown)="onKeydown($event, i)"
      >
        <span class="tt-tab__label" [attr.data-text]="tab.label">{{ tab.label }}</span>
      </button>
    </div>
  `,
})
export class TabsComponent {
  @Input() tabs: TabItem[] = [];
  @Input() activeId = '';
  @Input() ariaLabel = '';
  /** Prefix used to build stable, unique DOM ids for tab buttons (aria-labelledby target). */
  @Input() idPrefix = 'tt-tab';

  @Output() activeIdChange = new EventEmitter<string>();

  trackTab(_index: number, tab: TabItem): string {
    return tab.id;
  }

  tabDomId(tab: TabItem): string {
    return `${this.idPrefix}-${tab.id}`;
  }

  select(tab: TabItem): void {
    if (tab.disabled || tab.id === this.activeId) return;
    this.activeId = tab.id;
    this.activeIdChange.emit(tab.id);
  }

  /** Arrow Left/Right/Home/End move focus + activate (standard tabs keyboard pattern). */
  onKeydown(event: KeyboardEvent, index: number): void {
    const enabled = this.tabs
      .map((tab, i) => ({ tab, i }))
      .filter((entry) => !entry.tab.disabled);
    if (enabled.length === 0) return;

    let targetIndex: number | null = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      targetIndex = this.nextEnabledIndex(enabled.map((e) => e.i), index, 1);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      targetIndex = this.nextEnabledIndex(enabled.map((e) => e.i), index, -1);
    } else if (event.key === 'Home') {
      targetIndex = enabled[0].i;
    } else if (event.key === 'End') {
      targetIndex = enabled[enabled.length - 1].i;
    } else {
      return;
    }

    if (targetIndex === null) return;

    const focusIndex = targetIndex;
    event.preventDefault();
    const tab = this.tabs[focusIndex];
    const buttons = (event.currentTarget as HTMLElement)?.parentElement?.querySelectorAll<HTMLButtonElement>('.tt-tab');
    this.select(tab);
    queueMicrotask(() => {
      buttons?.[focusIndex]?.focus();
    });
  }

  private nextEnabledIndex(enabledIndices: number[], current: number, dir: 1 | -1): number {
    const pos = enabledIndices.indexOf(current);
    if (pos === -1) return enabledIndices[0];
    const nextPos = (pos + dir + enabledIndices.length) % enabledIndices.length;
    return enabledIndices[nextPos];
  }
}
